import { Component, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Check,
  Code2,
  FileDown,
  History,
  Loader2,
  Send,
  Settings2,
  TriangleAlert,
  UploadCloud,
  WandSparkles,
} from 'lucide-react';
import type {
  DocumentAcceptanceMode,
  DocumentAudience,
  DocumentTemplateDetail,
  DocumentTemplateKind,
  DocumentTemplateVersionInfo,
  DocumentVisibility,
  UpdateDocumentTemplateDto,
} from '@ip/shared-types';
import {
  DOCUMENT_ACCEPTANCE_MODES,
  DOCUMENT_AUDIENCES,
  DOCUMENT_TEMPLATE_KINDS,
  DOCUMENT_VISIBILITIES,
} from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDocumentsApi } from '@/services/api';
import { t } from '@/i18n';
import { renderPreviewHtml } from './markdownPreview';
import { VariablePalette, findUnknownVariablesClient } from './VariablePalette';
import { PublishDialog } from './PublishDialog';
import type { WysiwygApi } from './markdown-editor/MarkdownWysiwyg';

// Milkdown + remark pull in ~0.5 MB minified — loaded only when a template
// is actually opened in the (default) WYSIWYG mode, not with the main bundle.
const MarkdownWysiwyg = lazy(() =>
  import('./markdown-editor/MarkdownWysiwyg').then((m) => ({ default: m.MarkdownWysiwyg })),
);

const AUTOSAVE_DELAY_MS = 1500;

type SaveState = 'idle' | 'saving' | 'saved';
type EditorMode = 'wysiwyg' | 'raw';

/**
 * First lazy() chunk in the app: if it fails to load (stale SPA after a
 * deploy, network hiccup), a bare Suspense would white-screen the page.
 * The fallback keeps the template editable by dropping to raw mode.
 */
class WysiwygErrorBoundary extends Component<
  { onFallbackToRaw: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('WYSIWYG editor failed to load:', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 rounded-md border p-6 text-center">
        <p className="text-muted-foreground text-sm">{t('documents.editor.wysiwygLoadError')}</p>
        <Button variant="outline" size="sm" onClick={this.props.onFallbackToRaw}>
          <Code2 className="size-4" />
          {t('documents.editor.modeRaw')}
        </Button>
      </div>
    );
  }
}

interface TemplateEditorProps {
  nip: string;
  template: DocumentTemplateDetail;
  onTemplateChange: (template: DocumentTemplateDetail) => void;
}

/**
 * Template editor with two modes sharing one `content` state + autosave
 * (1.5s after typing stops):
 * - WYSIWYG (default): Milkdown rich-text editor emitting plain markdown —
 *   for users who don't know markdown (spec: RAW stays available, not default);
 * - RAW: the original split pane — monospace textarea + sanitized markdown-it
 *   preview.
 * Plus a variable palette sidebar and a toolbar for template settings,
 * PDF preview, version history and publishing.
 */
export function TemplateEditor({ nip, template, onTemplateChange }: TemplateEditorProps) {
  const { updateTemplate, publishVersion, previewPdf, listVersions } = useDocumentsApi();

  const [content, setContent] = useState(template.contentMd);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [mode, setMode] = useState<EditorMode>('wysiwyg');
  const lastSavedRef = useRef(template.contentMd);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wysiwygApiRef = useRef<WysiwygApi | null>(null);
  // Palette clicks made while the lazy WYSIWYG chunk is still loading are
  // queued here and flushed by onReady — otherwise they'd be silently lost.
  const pendingInsertsRef = useRef<{ kind: 'var' | 'text'; value: string }[]>([]);
  // Remount key for the WYSIWYG editor: bump whenever `content` may have
  // changed outside it (RAW edits), so it re-parses the markdown on return.
  const [wysiwygEpoch, setWysiwygEpoch] = useState(0);

  // Milkdown's markdownUpdated listener is debounced 200ms and its trailing
  // call dies with the editor — every flush point must pull the LIVE doc
  // first or the last typing burst is lost. Returns the freshest markdown.
  const syncFromWysiwyg = (): string => {
    const live = wysiwygApiRef.current?.getMarkdown();
    if (live != null && live !== contentRef.current) {
      contentRef.current = live;
      setContent(live);
      return live;
    }
    return contentRef.current;
  };

  // Kept in sync every render so the unmount flush below sees the latest
  // content/save-fn without re-running the effect (which would defeat it).
  const contentRef = useRef(content);
  contentRef.current = content;
  const flushRef = useRef<{ nip: string; id: string; save: typeof updateTemplate }>({
    nip,
    id: template.id,
    save: updateTemplate,
  });
  flushRef.current = { nip, id: template.id, save: updateTemplate };

  // Flush a pending debounced edit if the editor unmounts (e.g. the Back
  // button) before the 1.5s timer fires — otherwise the last keystrokes are
  // silently lost. Fire-and-forget; the SPA navigation keeps the request alive.
  useEffect(() => {
    return () => {
      const { nip: n, id, save } = flushRef.current;
      // Parent cleanups run before the child editor unmounts, so the live
      // WYSIWYG doc is still readable here — it may be ahead of contentRef
      // by the listener's 200ms debounce.
      const latest = wysiwygApiRef.current?.getMarkdown() ?? contentRef.current;
      if (latest !== lastSavedRef.current) {
        save(n, id, { contentMd: latest }).catch((error) => {
          console.error('Failed to flush unsaved template edits on unmount:', error);
        });
      }
    };
  }, []);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<DocumentTemplateVersionInfo[] | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Publish-to-audience dialog (Task 6) — distinct from "Publikuj wersję" above,
  // which only cuts a new immutable version; this creates a DocumentPublication.
  const [publicationDialogOpen, setPublicationDialogOpen] = useState(false);
  const canCreatePublication =
    template.currentVersion > 0 && template.category !== 'contractor' && !template.archivedAt;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Autosave: commit 1.5s after the user stops typing.
  useEffect(() => {
    if (content === lastSavedRef.current) return;
    setSaveState('saving');
    const timer = setTimeout(() => {
      updateTemplate(nip, template.id, { contentMd: content })
        .then((updated) => {
          lastSavedRef.current = content;
          setSaveState('saved');
          onTemplateChange(updated);
        })
        .catch((error) => {
          setSaveState('idle');
          toast.error(t('errors.saveFailed'));
          console.error(error);
        });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, nip, template.id]);

  const previewHtml = useMemo(() => renderPreviewHtml(content), [content]);
  const unknownVariables = useMemo(
    () => findUnknownVariablesClient(content, template.category, template.templateKind),
    [content, template.category, template.templateKind],
  );

  // Publish is rejected server-side for unknown variables or an empty draft;
  // block the CTA up front with the reason so users don't hit a raw 400.
  const publishBlockReason =
    unknownVariables.length > 0
      ? t('documents.editor.unknownVars')
      : content.trim().length === 0
        ? t('documents.editor.emptyDraft')
        : undefined;

  const insertVariable = (variable: string) => {
    if (mode === 'wysiwyg') {
      if (wysiwygApiRef.current) {
        wysiwygApiRef.current.insertVariable(variable);
      } else {
        // Chunk/editor still loading — queue, flushed by handleWysiwygReady.
        pendingInsertsRef.current.push({ kind: 'var', value: variable });
      }
      return;
    }
    const snippet = `{{${variable}}}`;
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => `${prev}${snippet}`);
      return;
    }
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? content.length;
    setContent(`${content.slice(0, start)}${snippet}${content.slice(end)}`);
    requestAnimationFrame(() => {
      const cursor = start + snippet.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  // Plain-text snippet insert (gender forms `{{plec:a/b}}`) — text, not chip.
  const insertText = (snippet: string) => {
    if (mode === 'wysiwyg') {
      if (wysiwygApiRef.current) {
        wysiwygApiRef.current.insertText(snippet);
      } else {
        pendingInsertsRef.current.push({ kind: 'text', value: snippet });
      }
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => `${prev}${snippet}`);
      return;
    }
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? content.length;
    setContent(`${content.slice(0, start)}${snippet}${content.slice(end)}`);
    requestAnimationFrame(() => {
      const cursor = start + snippet.length;
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleWysiwygReady = () => {
    const pending = pendingInsertsRef.current;
    pendingInsertsRef.current = [];
    pending.forEach((item) => {
      if (item.kind === 'var') wysiwygApiRef.current?.insertVariable(item.value);
      else wysiwygApiRef.current?.insertText(item.value);
    });
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;
    // Leaving WYSIWYG: pull the live doc so the textarea shows edits from
    // the last 200ms the debounced listener hasn't delivered yet.
    if (mode === 'wysiwyg') {
      syncFromWysiwyg();
      // Palette clicks still queued for a not-yet-ready editor would be lost
      // on unmount — append them as plain markdown instead.
      if (pendingInsertsRef.current.length > 0) {
        const snippets = pendingInsertsRef.current
          .map((item) => (item.kind === 'var' ? `{{${item.value}}}` : item.value))
          .join(' ');
        pendingInsertsRef.current = [];
        setContent((prev) => {
          const merged = `${prev.replace(/\n*$/, '\n\n')}${snippets}\n`;
          contentRef.current = merged;
          return merged;
        });
      }
    }
    // Entering WYSIWYG re-parses whatever the RAW textarea holds now.
    if (next === 'wysiwyg') setWysiwygEpoch((n) => n + 1);
    setMode(next);
  };

  // Ensures the draft on the server matches the editor before an operation
  // that reads it server-side (publish, PDF preview) — skips the round trip
  // when there's nothing pending.
  const flushPendingSave = async (): Promise<void> => {
    const latest = mode === 'wysiwyg' ? syncFromWysiwyg() : content;
    if (latest === lastSavedRef.current) return;
    const updated = await updateTemplate(nip, template.id, { contentMd: latest });
    lastSavedRef.current = latest;
    setSaveState('saved');
    onTemplateChange(updated);
  };

  const handleSettingChange = async (patch: UpdateDocumentTemplateDto) => {
    try {
      const updated = await updateTemplate(nip, template.id, patch);
      onTemplateChange(updated);
      toast.success(t('documents.templates.updated'));
    } catch (error) {
      toast.error(t('errors.saveFailed'));
      console.error(error);
    }
  };

  const openHistory = async (open: boolean) => {
    setHistoryOpen(open);
    if (open && versions === null) {
      try {
        setVersionsLoading(true);
        setVersions(await listVersions(nip, template.id));
      } catch (error) {
        toast.error(t('errors.loadingFailed'));
        console.error(error);
      } finally {
        setVersionsLoading(false);
      }
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      await flushPendingSave();
      const result = await publishVersion(nip, template.id);
      toast.success(t('documents.editor.published'));
      setPublishOpen(false);
      setVersions(null);
      onTemplateChange({ ...template, currentVersion: result.version, contentMd: lastSavedRef.current });
    } catch (error) {
      // Backend validation (unknown variables, empty draft) surfaces its own message.
      toast.error(error instanceof Error ? error.message : t('errors.saveFailed'));
      console.error(error);
    } finally {
      setPublishing(false);
    }
  };

  const handlePreviewPdf = async () => {
    try {
      setPreviewLoading(true);
      await flushPendingSave();
      const blob = await previewPdf(nip, template.id, {});
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewOpen(true);
    } catch (error) {
      toast.error(t('errors.pdfFailed'));
      console.error(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = (open: boolean) => {
    setPreviewOpen(open);
    if (!open && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-card flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3">
        <div className="flex items-center gap-3">
          <div className="bg-muted flex items-center gap-0.5 rounded-lg p-0.5">
            <Button
              type="button"
              variant={mode === 'wysiwyg' ? 'outline' : 'ghost'}
              size="sm"
              aria-pressed={mode === 'wysiwyg'}
              className={mode === 'wysiwyg' ? 'bg-background shadow-sm' : ''}
              onClick={() => switchMode('wysiwyg')}
            >
              <WandSparkles className="size-4" />
              {t('documents.editor.modeWysiwyg')}
            </Button>
            <Button
              type="button"
              variant={mode === 'raw' ? 'outline' : 'ghost'}
              size="sm"
              aria-pressed={mode === 'raw'}
              className={mode === 'raw' ? 'bg-background shadow-sm' : ''}
              onClick={() => switchMode('raw')}
            >
              <Code2 className="size-4" />
              {t('documents.editor.modeRaw')}
            </Button>
          </div>

          <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
            {saveState === 'saving' && (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {t('documents.editor.saving')}
              </>
            )}
            {saveState === 'saved' && (
              <>
                <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                {t('documents.editor.saved')}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="size-4" />
                {t('documents.editor.settings')}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <div className="flex flex-col gap-3">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('documents.templates.visibility')}
                  </Label>
                  <Select
                    value={template.visibility}
                    onValueChange={(value) =>
                      handleSettingChange({ visibility: value as DocumentVisibility })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_VISIBILITIES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`documents.templates.visibilities.${v}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('documents.templates.audience')}
                  </Label>
                  <Select
                    value={template.audience}
                    onValueChange={(value) =>
                      handleSettingChange({ audience: value as DocumentAudience })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_AUDIENCES.map((a) => (
                        <SelectItem key={a} value={a}>
                          {t(`documents.templates.audiences.${a}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('documents.templates.acceptance')}
                  </Label>
                  <Select
                    value={template.acceptanceMode}
                    onValueChange={(value) =>
                      handleSettingChange({ acceptanceMode: value as DocumentAcceptanceMode })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_ACCEPTANCE_MODES.map((a) => (
                        <SelectItem key={a} value={a}>
                          {t(`documents.templates.acceptances.${a}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {template.category === 'employee' && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs">
                      {t('documents.templates.templateKind')}
                    </Label>
                    <Select
                      value={template.templateKind ?? 'standard'}
                      onValueChange={(value) =>
                        handleSettingChange({ templateKind: value as DocumentTemplateKind })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TEMPLATE_KINDS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {t(`documents.templates.templateKinds.${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1">
                  <Label htmlFor="deadline-days" className="text-muted-foreground text-xs">
                    {t('documents.editor.deadlineDays')}
                  </Label>
                  <Input
                    id="deadline-days"
                    type="number"
                    min={1}
                    max={365}
                    defaultValue={template.defaultDeadlineDays ?? ''}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const value = raw === '' ? null : Number(raw);
                      if (value === template.defaultDeadlineDays) return;
                      handleSettingChange({ defaultDeadlineDays: value });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="include-logo" className="text-muted-foreground text-xs">
                    {t('documents.editor.logo')}
                  </Label>
                  <Switch
                    id="include-logo"
                    checked={template.includeLogo}
                    onCheckedChange={(checked) => handleSettingChange({ includeLogo: checked })}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={historyOpen} onOpenChange={openHistory}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <History className="size-4" />
                {t('documents.editor.history')}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64">
              {versionsLoading ? (
                <Loader2 className="text-muted-foreground mx-auto size-4 animate-spin" />
              ) : versions && versions.length > 0 ? (
                <ul className="flex flex-col gap-1.5 text-sm">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between">
                      <span className="font-medium">v{v.version}</span>
                      <span className="text-muted-foreground text-xs">
                        {new Date(v.publishedAt).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t('documents.editor.noVersions')}
                </p>
              )}
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={handlePreviewPdf} disabled={previewLoading}>
            {previewLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileDown className="size-4" />
            )}
            {t('documents.editor.preview')}
          </Button>

          <Button
            size="sm"
            onClick={() => setPublishOpen(true)}
            disabled={!!publishBlockReason}
            title={publishBlockReason}
          >
            <UploadCloud className="size-4" />
            {t('documents.editor.publish')}
          </Button>

          {canCreatePublication && (
            <Button size="sm" variant="outline" onClick={() => setPublicationDialogOpen(true)}>
              <Send className="size-4" />
              {t('documents.publications.publish')}
            </Button>
          )}
        </div>
      </div>

      {unknownVariables.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-px size-3.5 shrink-0" />
          <span>
            {t('documents.editor.unknownVars')}:{' '}
            <span className="font-mono">
              {unknownVariables.map((v) => (v.startsWith('{{') ? v : `{{${v}}}`)).join(', ')}
            </span>
          </span>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
        {mode === 'wysiwyg' ? (
          // switchMode (not bare setMode) so palette inserts queued while the
          // chunk was loading are flushed into the textarea, not dropped.
          <WysiwygErrorBoundary onFallbackToRaw={() => switchMode('raw')}>
            <Suspense
              fallback={
                <div className="flex min-h-[70vh] items-center justify-center rounded-md border">
                  <Loader2 className="text-muted-foreground size-6 animate-spin" />
                </div>
              }
            >
              <MarkdownWysiwyg
                key={wysiwygEpoch}
                defaultValue={content}
                onChange={setContent}
                apiRef={wysiwygApiRef}
                onReady={handleWysiwygReady}
              />
            </Suspense>
          </WysiwygErrorBoundary>
        ) : (
          <div className="grid min-h-[70vh] gap-3 md:grid-cols-2">
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              placeholder={t('documents.editor.pagebreakHint')}
              className="h-full min-h-[70vh] resize-none font-mono text-sm"
            />
            <div className="bg-background h-full min-h-[70vh] overflow-auto rounded-md border p-4">
              <div className="doc-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        )}

        <div className="bg-card h-fit rounded-md border p-3">
          <p className="mb-2 text-sm font-medium">{t('documents.editor.variables')}</p>
          <VariablePalette
            category={template.category}
            templateKind={template.templateKind}
            onInsert={insertVariable}
            onInsertText={insertText}
          />
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .doc-preview h1 { font-size: 1.25rem; font-weight: 600; margin: 0.75rem 0 0.5rem; }
            .doc-preview h2 { font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.5rem; }
            .doc-preview h3 { font-size: 1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
            .doc-preview p { margin: 0.5rem 0; font-size: 0.875rem; line-height: 1.5; }
            .doc-preview ul, .doc-preview ol { margin: 0.5rem 0; padding-left: 1.5rem; font-size: 0.875rem; }
            .doc-preview table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; font-size: 0.8125rem; }
            .doc-preview th, .doc-preview td { border: 1px solid var(--color-border, #e4e4e7); padding: 0.25rem 0.5rem; text-align: left; }
            .doc-preview th { background: var(--color-border-light, #f4f4f5); }
            .doc-preview blockquote { border-left: 2px solid var(--color-border, #e4e4e7); margin: 0.5rem 0; padding-left: 0.75rem; font-style: italic; color: var(--color-muted-foreground, #71717a); }
            .doc-preview hr.pagebreak-marker { border: none; border-top: 1px dashed var(--color-border, #e4e4e7); margin: 1rem 0; }
            .doc-preview hr:not(.pagebreak-marker) { border: none; border-top: 1px solid var(--color-border, #e4e4e7); margin: 0.75rem 0; }
            .doc-preview mark { background: rgba(234, 179, 8, 0.35); border-radius: 2px; padding: 0 2px; }

            /* WYSIWYG (Milkdown/ProseMirror) — shares .doc-preview typography above. */
            .md-wysiwyg { white-space: pre-wrap; word-wrap: break-word; caret-color: var(--color-foreground, currentColor); }
            /* --primary/--ring are the theme-switched shadcn vars (index.css
               :root + .dark); the legacy --color-primary is a STATIC light
               value and must not be used here. */
            .md-wysiwyg .template-var-chip {
              display: inline-block; padding: 0 0.4em; margin: 0 1px; border-radius: 9999px;
              border: 1px solid var(--primary, #6d28d9); color: var(--primary, #6d28d9);
              background: color-mix(in srgb, var(--primary, #6d28d9) 10%, transparent);
              font-family: var(--font-mono, monospace); font-size: 0.8em; line-height: 1.6;
              user-select: none; cursor: default; white-space: nowrap;
            }
            .md-wysiwyg .template-var-chip::before { content: '{{'; opacity: 0.5; }
            .md-wysiwyg .template-var-chip::after { content: '}}'; opacity: 0.5; }
            .md-wysiwyg .pagebreak-block {
              display: flex; align-items: center; justify-content: center; gap: 0.75rem;
              margin: 1rem 0; min-height: 1.25rem; user-select: none;
              color: var(--muted-foreground, #71717a); font-size: 0.75rem;
            }
            .md-wysiwyg .pagebreak-block::before, .md-wysiwyg .pagebreak-block::after {
              content: ''; flex: 1; border-top: 1px dashed var(--border, #e4e4e7);
            }
            .md-wysiwyg .ProseMirror-selectednode { outline: 2px solid var(--ring, #a78bfa); outline-offset: 1px; border-radius: 4px; }
            .md-wysiwyg th, .md-wysiwyg td { min-width: 4rem; }
          `,
        }}
      />

      <AlertDialog open={publishOpen} onOpenChange={(open) => !publishing && setPublishOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.editor.publish')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.editor.publishConfirm', { version: template.currentVersion + 1 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={publishing} onClick={handlePublish}>
              {publishing && <Loader2 className="size-4 animate-spin" />}
              {t('documents.editor.publish')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="flex h-[85vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle>
              {t('documents.editor.preview')} — {template.name}
            </DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe src={previewUrl} className="h-full w-full flex-1 border-0" title="PDF preview" />
          )}
        </DialogContent>
      </Dialog>

      {canCreatePublication && (
        <PublishDialog
          nip={nip}
          open={publicationDialogOpen}
          onOpenChange={setPublicationDialogOpen}
          template={template}
          onPublished={() => setPublicationDialogOpen(false)}
        />
      )}
    </div>
  );
}
